import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { UNIT_LABEL, type Unit } from '@artmozaik/shared';
import { api, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import FaturaCekmece from '../ui/FaturaCekmece';
import { BelgeAkisi, type IrsaliyeDurum, type ResmiDurum } from '../ui/ResmiBelge';
import TanimliSecim from '../ui/TanimliSecim';
import { VadeCekmece, useVadeler } from '../ui/VadeTanim';
import { TahsilCekmece, useKasaHesaplari } from '../ui/KasaHesapSec';
import { sor } from '../ui/Sor';

type Odeme = {
  id: number; sira: number; etiket: string; taban: string; oranPct: number;
  tutarKurus: number; odenenKurus: number; kalanKurus: number;
  vadeTarihi: string | null; bekledigi: 'fatura' | 'sevk' | null;
  durum: 'ODENDI' | 'GECIKTI' | 'BUGUN' | 'BEKLIYOR' | 'BASLAMADI'; gecenGun: number | null;
  kaynak: 'PARASUT' | 'ELLE' | null; odendiAt: string | null; not: string | null; isaretleyen: string | null;
};
type Detay = {
  id: number; no: string; title: string; status: string; statusLabel: string;
  asamalar: { id: string; ad: string }[];
  cari: { id: number; name: string; phone: string | null };
  source: string; dueDate: string | null; note: string | null; gecikti: boolean;
  createdAt: string; updatedAt: string;
  iptal: boolean; iptalTarihi: string | null; iptalNedeni: string | null; iptalEden: string | null;
  fatura: { kesildi: boolean; invoicedAt: string | null; kesen: string | null; parasuttenIzleniyor: boolean; no?: string; toplamKurus?: number; kalanKurus?: number };
  resmi: ResmiDurum; irsaliye: IrsaliyeDurum;
  teklif: { id: number; no: string; status: string; statusLabel: string } | null;
  kalemler: { id: number; productId: number | null; kod: string; ad: string; miktar: number; birim: string | null; listeKurus?: number; iskontoPct?: number; kdvPct?: number; netKurus?: number }[];
  toplam: { araToplamKurus: number; kdvToplamKurus: number; genelToplamKurus: number } | null;
  ic?: { maliyetKurus: number; karKurus: number; karYuzde: number | null; maliyetiBilinmeyenSatir: number };
  vade: { id: string; ad: string; etiket: string } | null;
  tutarKurus: number | null;
  odemeler: Odeme[];
  odemeOzet: { toplamKurus: number; odenenKurus: number; kalanKurus: number; gecikmisAdet: number; gecikmisKurus: number };
  iz: { id: number; action: string; etiket: string; kullanici: string; at: string; ayrinti: string | null }[];
};

const KAYNAK_AD: Record<string, string> = { MAIL: 'Mail', TELEFON: 'Telefon', YUZYUZE: 'Yüz yüze', TEKLIF: 'Teklif' };
const gunGirdisi = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('sv-SE', { timeZone: 'Europe/Istanbul' }) : '');

/** Parçanın durumu: ödendi / vadesi geçti (n gün) / bugün / bekliyor / olay bekleniyor. */
function OdemeRozet({ p }: { p: Odeme }) {
  if (p.durum === 'ODENDI') return <span className="ccd-badge ccd-badge-success">{p.kaynak === 'PARASUT' ? 'ödendi (Paraşüt)' : 'ödendi'}</span>;
  if (p.durum === 'GECIKTI') return <span className="ccd-badge ccd-badge-danger">vadesi geçti ({p.gecenGun} gün)</span>;
  if (p.durum === 'BUGUN') return <span className="ccd-badge ccd-badge-warning">vadesi bugün</span>;
  if (p.durum === 'BASLAMADI') return <span className="ccd-badge">{p.bekledigi === 'fatura' ? 'fatura bekleniyor' : 'sevk bekleniyor'}</span>;
  return <span className="ccd-badge">bekliyor{p.gecenGun != null ? ` · ${-p.gecenGun} gün var` : ''}</span>;
}

/**
 * SİPARİŞ DETAYI — "Siparişe tıklayınca cariye gidiyor; ben siparişin içine
 * girmek istiyorum." Tek sayfada: süreç, kalemler, ödeme planı ve vade takibi,
 * fatura, termin/not, kayıt izi. Maliyet satırlarda YOK; kâr özeti yalnız
 * yetkisi olana API'den gelir.
 */
export default function SiparisDetay() {
  const { id } = useParams();
  const { can } = useAuth();
  const yazar = can('order.write');
  const [d, setD] = useState<Detay | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState(false);
  const [faturaAcik, setFaturaAcik] = useState(false);
  /* Termin ve not: yerinde düzenlenir, "Kaydet" ile yazılır. */
  const [termin, setTermin] = useState('');
  const [not, setNot] = useState('');
  /* Ödeme planı formu (plan yokken ya da vade değiştirilirken). */
  const [planAcik, setPlanAcik] = useState(false);
  const [vadeId, setVadeId] = useState('');
  const [tutar, setTutar] = useState('');
  const [vadeTanimAcik, setVadeTanimAcik] = useState(false);
  /** "Tahsil edildi" çekmecesi (not + hangi kasaya girdi) — yalnız kasaya yazabilen rolde. */
  const kasa = useKasaHesaplari();
  const [tahsil, setTahsil] = useState<Odeme | null>(null);
  const { veri: vadeler, setVeri: setVadeler } = useVadeler();

  const yukle = useCallback(async () => {
    const r = await api<Detay>(`/api/siparis/${id}`);
    setD(r); setTermin(gunGirdisi(r.dueDate)); setNot(r.note ?? '');
    setVadeId(r.vade?.id ?? ''); setTutar(r.tutarKurus != null ? String(r.tutarKurus / 100) : '');
  }, [id]);
  useEffect(() => { setD(null); yukle().catch((e) => setHata((e as Error).message)); }, [yukle]);

  /** Ortak yazma sarmalayıcısı: hata/bilgi şeridi ve yeniden yükleme. */
  async function yap(is: () => Promise<string | null | void>, hataMetni: string) {
    setHata(null); setBilgi(null); setBekliyor(true);
    try { const m = await is(); await yukle(); if (m) setBilgi(m); }
    catch (e) { setHata(e instanceof Error ? e.message : hataMetni); }
    finally { setBekliyor(false); }
  }

  if (hata && !d) {
    return (
      <>
        <Link to="/siparis" className="ccd-meta">← Sipariş hattı</Link>
        <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>
      </>
    );
  }
  if (!d) return <div className="ccd-inline-note">Yükleniyor…</div>;

  const sira = d.asamalar.findIndex((a) => a.id === d.status);
  const sevkSira = d.asamalar.findIndex((a) => a.id === 'SEVK');
  const onceki = d.asamalar[sira - 1];
  const sonraki = d.asamalar[sira + 1];
  const odenmemisOnOdeme = d.odemeler.filter((p) => p.durum !== 'ODENDI' && (p.taban === 'PESIN' || p.taban === 'SEVK_ONCESI'));
  const odenenVar = d.odemeler.some((p) => p.kaynak === 'ELLE');
  const kirli = termin !== gunGirdisi(d.dueDate) || not !== (d.note ?? '');

  function tasi(hedef: { id: string; ad: string }) {
    const hedefSira = d!.asamalar.findIndex((a) => a.id === hedef.id);
    // Sevk öncesi alınacak ödeme duruyorsa UYAR — ama engelleme (karar kullanıcının).
    const uyar = hedefSira >= sevkSira && sira < sevkSira && odenmemisOnOdeme.length > 0;
    const soru = uyar
      ? `DİKKAT: sevk öncesi alınması gereken ${formatTl(odenmemisOnOdeme.reduce((t, p) => t + p.kalanKurus, 0))} ödeme henüz tahsil edilmedi.\n\n${d!.no} yine de "${hedef.ad}" aşamasına geçirilsin mi?`
      : `${d!.no} "${hedef.ad}" aşamasına taşınsın mı?`;
    if (!window.confirm(soru)) return;
    void yap(async () => {
      const r = await api<{ uyari?: string | null }>(`/api/siparis/${d!.id}/durum`, { method: 'POST', body: JSON.stringify({ status: hedef.id }) });
      return r.uyari ?? `${hedef.ad} aşamasına geçirildi.`;
    }, 'Taşınamadı');
  }
  async function iptalEt() {
    const neden = await sor({
      baslik: 'Siparişi iptal et', alt: d!.no, aciklama: 'Sipariş panodan düşer; "İptal edilenler"de nedeniyle durur ve geri alınabilir.',
      etiket: 'İptal nedeni', yerTutucu: 'Müşteri vazgeçti, stok yok, fiyat…', zorunlu: true, azami: 300, onay: 'Siparişi iptal et', tehlike: true,
    });
    if (neden == null) return;
    void yap(async () => {
      const r = await api<{ uyari: string | null }>(`/api/siparis/${d!.id}/iptal`, { method: 'POST', body: JSON.stringify({ neden }) });
      return `${d!.no} iptal edildi.${r.uyari ? ` ${r.uyari}` : ''}`;
    }, 'İptal edilemedi');
  }
  async function odemeIsaretle(p: Odeme, odendi: boolean) {
    let notu: string | null = null;
    // Kasaya yazabilen rolde not + "hangi kasaya girdi" çekmecesi; diğerlerinde eski soru penceresi.
    if (odendi && kasa.acik) { setTahsil(p); return; }
    if (odendi) {
      notu = await sor({
      baslik: 'Tahsil edildi olarak işaretle', alt: d!.no,
      aciklama: <>{p.etiket} · <b>{formatTl(p.tutarKurus)}</b> tahsil edildi olarak işaretlenecek.</>,
      etiket: 'Not', yerTutucu: 'Havale, çek, nakit…', azami: 300, onay: 'Tahsil edildi',
    });
      if (notu == null) return;
    } else if (!window.confirm(`${p.etiket} · ${formatTl(p.tutarKurus)} için "tahsil edildi" işareti geri alınsın mı?`)) return;
    void yap(async () => {
      await api(`/api/siparis/${d!.id}/odeme/${p.id}`, { method: 'POST', body: JSON.stringify({ odendi, not: notu || null }) });
      return odendi ? `${p.etiket} tahsil edildi olarak işaretlendi.` : 'İşaret geri alındı.';
    }, 'Ödeme işaretlenemedi');
  }
  function planKur(e: React.FormEvent) {
    e.preventDefault();
    void yap(async () => {
      await api(`/api/siparis/${d!.id}/odeme-plani`, {
        method: 'POST',
        body: JSON.stringify({ vadeId, ...(tutar.trim() ? { tutarTl: Number(tutar.replace(',', '.')) } : {}) }),
      });
      setPlanAcik(false);
      return 'Ödeme planı kuruldu.';
    }, 'Ödeme planı kurulamadı');
  }

  return (
    <>
      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <Link to="/siparis" className="ccd-meta">← Sipariş hattı</Link>
          <h1 className="ccd-title">
            Sipariş <span className="ccd-mono">{d.no}</span> · {d.iptal ? <s>{d.title}</s> : d.title}
          </h1>
          <div className="ccd-meta">
            <Link to={`/crm/${d.cari.id}`} title="Cari kartı"><b>{d.cari.name}</b></Link>
            {' '}· <span className={`ccd-badge${d.iptal ? ' ccd-badge-danger' : ''}`}>{d.iptal ? 'iptal edildi' : d.statusLabel}</span>
            {' '}· {KAYNAK_AD[d.source] ?? d.source} · {formatTarih(d.createdAt)} tarihinde açıldı
            {d.teklif && <> · teklif <Link to={`/teklif/${d.teklif.id}`} className="ccd-mono">{d.teklif.no}</Link></>}
          </div>
        </div>
        <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {!d.iptal && !d.fatura.kesildi && d.teklif && can('settings.write') && (
            <button className="ccd-btn ccd-btn-sm" onClick={() => setFaturaAcik(true)}
                    title="Teklifteki satırlarla Paraşüt'te satış faturası açar">Faturalandır</button>
          )}
          {yazar && !d.iptal && d.status !== 'KAPANDI' && (
            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" style={{ color: 'var(--ccd-danger)' }} disabled={bekliyor}
                    title="Siparişi iptal et (neden sorulur)" onClick={iptalEt}>İptal et</button>
          )}
        </div>
      </div>

      {d.iptal && (
        <div className="ccd-banner ccd-banner-danger">
          <span className="ccd-banner-icon">!</span>
          <div style={{ flex: 1 }}>
            <b>Bu sipariş iptal edildi</b>{d.iptalTarihi ? ` · ${formatTarih(d.iptalTarihi)}` : ''}{d.iptalEden ? ` · ${d.iptalEden}` : ''} · {d.statusLabel} aşamasındaydı.
            {d.iptalNedeni && <div><b>Neden:</b> {d.iptalNedeni}</div>}
            {d.fatura.kesildi && <div>Paraşüt'te faturası var; orada ayrıca iptal edilmeli.</div>}
            <div>İptal edilen siparişin ödemeleri hatırlatılmaz.</div>
          </div>
          {yazar && (
            <button className="ccd-btn ccd-btn-sm" disabled={bekliyor} title="Siparişi kaldığı aşamada yeniden açar"
                    onClick={() => void yap(async () => { await api(`/api/siparis/${d.id}/iptal-geri-al`, { method: 'POST' }); return `${d.no} yeniden açıldı.`; }, 'Geri alınamadı')}>
              İptali geri al
            </button>
          )}
        </div>
      )}
      {bilgi && <div className="ccd-banner"><span className="ccd-banner-icon">✓</span><span>{bilgi}</span></div>}
      {hata && <div className="ccd-banner ccd-banner-danger"><span className="ccd-banner-icon">!</span><span>{hata}</span></div>}

      {/* Süreç çubuğu: geçilen aşamalar dolu, şimdiki vurgulu. Aşamaya tıklayınca oraya taşınır. */}
      <div className={`ccd-card ccd-stack-sm${d.iptal ? ' ccd-sdetay-soluk' : ''}`}>
        <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="ccd-fieldset-title">Süreç <Ipucu id="siparis-asamalari" /></div>
          {yazar && !d.iptal && (
            <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {onceki && <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={bekliyor} onClick={() => tasi(onceki)}>← {onceki.ad} aşamasına geri al</button>}
              {sonraki && <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={bekliyor} onClick={() => tasi(sonraki)}>{sonraki.ad} aşamasına geçir →</button>}
            </div>
          )}
        </div>
        <ol className="ccd-surec" aria-label={`${d.no} süreç aşaması`}>
          {d.asamalar.map((a, n) => (
            <li key={a.id} className={n < sira ? 'ccd-surec-gecti' : n === sira ? 'ccd-surec-simdi' : ''}>
              <button type="button" disabled={!yazar || d.iptal || bekliyor || n === sira}
                      title={n === sira ? 'Şu anki aşama' : `${a.ad} aşamasına taşı`} onClick={() => tasi(a)}>
                <span className="ccd-surec-nokta">{n < sira ? '✓' : n + 1}</span>
                <span className="ccd-surec-ad">{a.ad}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <div className="ccd-sdetay-izgara">
        {/* SOL: kalemler ve toplamlar */}
        <div className="ccd-stack">
          <div className="ccd-card ccd-stack-sm">
            <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div className="ccd-fieldset-title">Kalemler{d.kalemler.length > 0 ? ` (${d.kalemler.length})` : ''}</div>
              {d.teklif && <Link to={`/teklif/${d.teklif.id}`} className="ccd-meta">Teklif {d.teklif.no} ({d.teklif.statusLabel.toLowerCase()}) →</Link>}
            </div>
            {d.kalemler.length === 0 ? (
              <div className="ccd-empty">Bu sipariş bir tekliften açılmadı; kalem listesi yok.</div>
            ) : (
              <div className="ccd-table-wrap">
                <table className="ccd-table">
                  <thead><tr>
                    <th className="ccd-th">Kod / Ürün</th><th className="ccd-th ccd-td-num">Miktar</th>
                    {d.toplam && <><th className="ccd-th ccd-td-num">Birim fiyat</th><th className="ccd-th ccd-td-num">İsk. % <Ipucu id="satis-iskontosu" /></th><th className="ccd-th ccd-td-num">Tutar <Ipucu id="teklif-toplam" /></th></>}
                  </tr></thead>
                  <tbody>
                    {d.kalemler.map((k) => (
                      <tr key={k.id} className="ccd-tr">
                        <td className="ccd-td">
                          {k.productId ? <Link to={`/stok/${k.productId}`} className="ccd-mono">{k.kod}</Link> : <span className="ccd-mono">{k.kod}</span>}
                          <div className="ccd-meta">{k.ad}</div>
                        </td>
                        <td className="ccd-td ccd-td-num">{k.miktar}{k.birim ? ` ${UNIT_LABEL[k.birim as Unit] ?? k.birim.toLowerCase()}` : ''}</td>
                        {d.toplam && (
                          <>
                            <td className="ccd-td ccd-td-num">{formatTl(k.listeKurus ?? 0)}</td>
                            <td className="ccd-td ccd-td-num">%{k.iskontoPct}</td>
                            <td className="ccd-td ccd-td-num"><b>{formatTl(k.netKurus ?? 0)}</b></td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {d.toplam && (
              <div className="ccd-sdetay-toplam" data-fihrist="teklif-toplam">
                <span>Ara toplam</span><span>{formatTl(d.toplam.araToplamKurus)}</span>
                <span>KDV</span><span>{formatTl(d.toplam.kdvToplamKurus)}</span>
                <b>Genel toplam</b><b>{formatTl(d.toplam.genelToplamKurus)}</b>
              </div>
            )}
            {d.ic && (
              <div className="ccd-meta ccd-sdetay-ic" data-fihrist="kar-marj">
                Yalnız siz görürsünüz <Ipucu id="kar-marj" /> ·{' '}
                {d.ic.maliyetiBilinmeyenSatir > 0 && d.ic.maliyetKurus === 0
                  ? 'kâr hesaplanamıyor (alış fiyatı bilinmiyor)'
                  : <>tahmini maliyet {formatTl(d.ic.maliyetKurus)} · <b>kâr {formatTl(d.ic.karKurus)}{d.ic.karYuzde != null ? ` (%${d.ic.karYuzde})` : ''}</b>
                      {d.ic.maliyetiBilinmeyenSatir > 0 && ` · ${d.ic.maliyetiBilinmeyenSatir} satırın alış fiyatı bilinmiyor, kâr eksik`}</>}
              </div>
            )}
          </div>

          {/* Kayıt izi: kim, ne zaman, hangi aşamadan hangisine */}
          <div className="ccd-card ccd-stack-sm">
            <div className="ccd-fieldset-title">Kayıt izi</div>
            {d.iz.length === 0 ? <div className="ccd-meta">Kayıt yok.</div> : (
              <ul className="ccd-sdetay-iz">
                {d.iz.map((k) => (
                  <li key={k.id}>
                    <span className="ccd-meta">{formatTarih(k.at)}</span>
                    <span><b>{k.etiket}</b>{k.ayrinti ? ` · ${k.ayrinti}` : ''}</span>
                    <span className="ccd-meta">{k.kullanici}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* SAĞ: ödeme planı, fatura, termin ve not */}
        <div className="ccd-stack">
          <div className="ccd-card ccd-stack-sm" data-tur="odeme-plani">
            <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div className="ccd-fieldset-title">Ödeme planı <Ipucu id="odeme-plani" /></div>
                <div className="ccd-meta">{d.vade ? d.vade.etiket : 'Vade belirtilmedi'}{d.tutarKurus != null ? ` · ${formatTl(d.tutarKurus)}` : ''}</div>
              </div>
              {yazar && !d.iptal && d.odemeler.length > 0 && !planAcik && (
                <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={odenenVar}
                        title={odenenVar ? 'Tahsil edilmiş parça var; önce işareti geri alın' : 'Vadeyi ya da tutarı değiştir'}
                        onClick={() => setPlanAcik(true)}>Vadeyi değiştir</button>
              )}
            </div>

            {d.odemeOzet.gecikmisAdet > 0 && !d.iptal && (
              <div className="ccd-banner ccd-banner-danger">
                <span className="ccd-banner-icon">!</span>
                <span><b>{formatTl(d.odemeOzet.gecikmisKurus)}</b> ödemenin vadesi geçti — müşteriden isteyin.</span>
              </div>
            )}
            {d.fatura.parasuttenIzleniyor && (
              <div className="ccd-meta">Ödeme Paraşüt'teki faturadan kendiliğinden izleniyor{d.fatura.kalanKurus != null ? ` · faturada kalan ${formatTl(d.fatura.kalanKurus)}` : ''}.</div>
            )}

            {d.odemeler.length > 0 && (
              <div className="ccd-vade-plan">
                {d.odemeler.map((p) => (
                  <div key={p.id} className={`ccd-vade-plan-satir${p.durum === 'GECIKTI' && !d.iptal ? ' ccd-vade-gecikti' : ''}${p.durum === 'ODENDI' ? ' ccd-vade-odendi' : ''}`}>
                    <div style={{ minWidth: 0 }}>
                      <div><b>{p.etiket}</b> · <b>{formatTl(p.tutarKurus)}</b></div>
                      <div className="ccd-meta">
                        {p.vadeTarihi ? `Vade ${formatTarih(p.vadeTarihi)}`
                          : p.bekledigi === 'fatura' ? 'Fatura kesilince başlar' : 'Sevk aşamasına geçirilirken istenir'}
                        {p.durum !== 'ODENDI' && p.odenenKurus > 0 && ` · ${formatTl(p.odenenKurus)} ödendi, kalan ${formatTl(p.kalanKurus)}`}
                        {p.kaynak === 'ELLE' && p.odendiAt && ` · ${formatTarih(p.odendiAt)} tahsil edildi${p.isaretleyen ? ` (${p.isaretleyen})` : ''}`}
                        {p.kaynak === 'ELLE' && p.not && ` · ${p.not}`}
                      </div>
                    </div>
                    <div className="ccd-row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <OdemeRozet p={p} />
                      {yazar && !d.iptal && p.durum !== 'ODENDI' && (
                        <button className="ccd-btn ccd-btn-sm" disabled={bekliyor} onClick={() => odemeIsaretle(p, true)}>Tahsil edildi</button>
                      )}
                      {yazar && !d.iptal && p.kaynak === 'ELLE' && (
                        <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" disabled={bekliyor} onClick={() => odemeIsaretle(p, false)}>Geri al</button>
                      )}
                    </div>
                  </div>
                ))}
                {d.odemeler.length > 1 && (
                  <div className="ccd-meta">Tahsil edilen {formatTl(d.odemeOzet.odenenKurus)} · kalan <b>{formatTl(d.odemeOzet.kalanKurus)}</b></div>
                )}
              </div>
            )}

            {d.odemeler.length === 0 && !planAcik && (
              <div className="ccd-meta">
                {d.teklif ? 'Teklifte vade seçilmediği için ödeme planı yok.' : 'Teklifsiz açılan siparişte tutar bilinmez; plan elle kurulur.'}
                {yazar && !d.iptal && <> <button type="button" className="ccd-btn ccd-btn-sm" style={{ marginLeft: 6 }} onClick={() => setPlanAcik(true)}>Vade seç, plan kur</button></>}
              </div>
            )}

            {planAcik && yazar && (
              <form className="ccd-vade-form ccd-stack-sm" onSubmit={planKur}>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="sd-vade">Vade<span className="ccd-required"> *</span></label>
                  <TanimliSecim id="sd-vade" deger={vadeId} degis={setVadeId} yukleniyor={!vadeler}
                                secenekler={(vadeler?.secenekler ?? []).map((s) => ({ id: s.id, ad: s.ad }))}
                                bosEtiket="— vade seçin —" ekSecenek={d.vade ? { id: d.vade.id, ad: d.vade.ad } : null}
                                tanimlayabilir={can('settings.write')} yeniTanimla={() => setVadeTanimAcik(true)} yeniEtiket="+ Yeni vade tanımla…" />
                </div>
                <div className="ccd-field">
                  <label className="ccd-field-label" htmlFor="sd-tutar">Sipariş tutarı (₺, KDV dahil){!d.teklif && <span className="ccd-required"> *</span>}</label>
                  <input id="sd-tutar" className="ccd-input" type="number" inputMode="decimal" step="any" min={0} required={!d.teklif} value={tutar}
                         onChange={(e) => setTutar(e.target.value)} placeholder={d.toplam ? String(d.toplam.genelToplamKurus / 100) : 'Örn. 12500'} />
                  {d.teklif && <div className="ccd-field-hint">Boş bırakılırsa teklifin genel toplamı alınır.</div>}
                </div>
                <div className="ccd-row" style={{ gap: 8 }}>
                  <button className="ccd-btn ccd-btn-primary ccd-btn-sm" disabled={bekliyor || !vadeId}>Planı kur</button>
                  <button type="button" className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setPlanAcik(false)}>Vazgeç</button>
                </div>
              </form>
            )}
          </div>

          {/* Belge akışı: (irsaliye) → fatura kaydı → resmîleştir → PDF — hepsi panelden. */}
          <BelgeAkisi siparis={d} yenile={() => { void yukle().catch(() => undefined); }}
                      faturalandir={can('settings.write') ? () => setFaturaAcik(true) : undefined} />

          <div className="ccd-card ccd-stack-sm">
            <div className="ccd-fieldset-title">Fatura</div>
            {d.fatura.kesildi ? (
              <div>
                <span className="ccd-badge ccd-badge-success">faturalandı</span>
                <div className="ccd-meta" style={{ marginTop: 6 }}>
                  {d.fatura.invoicedAt ? formatTarih(d.fatura.invoicedAt) : ''}{d.fatura.kesen ? ` · ${d.fatura.kesen}` : ''}
                  {d.fatura.no ? ` · fatura no ${d.fatura.no}` : ' · Paraşüt\'te açıldı; panele düşünce ödemesi kendiliğinden izlenir'}
                  {d.fatura.toplamKurus != null && ` · ${formatTl(d.fatura.toplamKurus)}`}
                </div>
              </div>
            ) : (
              <div className="ccd-meta">
                Henüz faturalanmadı.
                {!d.teklif ? ' Faturalandırmak için sipariş bir tekliften açılmış olmalı.'
                  : !d.iptal && can('settings.write') && (
                    <> <button type="button" className="ccd-btn ccd-btn-sm" style={{ marginLeft: 6 }} onClick={() => setFaturaAcik(true)}>Faturalandır</button></>
                  )}
              </div>
            )}
          </div>

          <form className="ccd-card ccd-stack-sm"
                onSubmit={(e) => {
                  e.preventDefault();
                  void yap(async () => {
                    await api(`/api/siparis/${d.id}`, { method: 'PATCH', body: JSON.stringify({ dueDate: termin || null, note: not.trim() || null }) });
                    return 'Termin ve not kaydedildi.';
                  }, 'Kaydedilemedi');
                }}>
            <div className="ccd-fieldset-title">Termin ve not</div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="sd-termin">Termin tarihi</label>
              <input id="sd-termin" className="ccd-input" type="date" value={termin} disabled={!yazar} onChange={(e) => setTermin(e.target.value)} />
              {d.gecikti && <div className="ccd-field-hint ccd-kpi-down">Termin geçti.</div>}
            </div>
            <div className="ccd-field">
              <label className="ccd-field-label" htmlFor="sd-not">Not</label>
              <textarea id="sd-not" className="ccd-textarea" rows={3} value={not} disabled={!yazar} onChange={(e) => setNot(e.target.value)}
                        placeholder="Teslimat adresi, özel istek, müşteriyle konuşulanlar…" />
            </div>
            {yazar && <button className="ccd-btn ccd-btn-sm" style={{ alignSelf: 'flex-start' }} disabled={bekliyor || !kirli}>Kaydet</button>}
          </form>
        </div>
      </div>

      {faturaAcik && (
        <FaturaCekmece siparisId={d.id} kapat={() => setFaturaAcik(false)} kesildi={() => { void yukle().catch(() => undefined); }} />
      )}
      {vadeTanimAcik && (
        <VadeCekmece kapat={() => setVadeTanimAcik(false)}
                     eklendi={(yeni, liste) => { setVadeler(liste); setVadeId(yeni.id); setVadeTanimAcik(false); }} />
      )}
      {tahsil && (
        <TahsilCekmece baslik={`${d.no} · ${tahsil.etiket}`} tutarKurus={tahsil.tutarKurus} hesaplar={kasa.hesaplar} kapat={() => setTahsil(null)}
                       onayla={async (v) => {
                         const r = await api<{ kasaHareketId: number | null }>(`/api/siparis/${d.id}/odeme/${tahsil.id}`, {
                           method: 'POST', body: JSON.stringify({ odendi: true, not: v.not, ...(v.hesapId ? { hesapId: v.hesapId, islemAnahtari: v.islemAnahtari } : {}) }),
                         });
                         await yukle();
                         setHata(null);
                         setBilgi(`${tahsil.etiket} tahsil edildi olarak işaretlendi.${r.kasaHareketId ? ' Kasa girişi yazıldı.' : ''}`);
                       }} />
      )}
    </>
  );
}
